/**
 * app/components/SubscriptionStatus.tsx
 * -------------------------------------
 * What: Displays the user's subscription tier and relevant actions.
 * Why: Central place to surface upgrade/renewal cues and status.
 */
// SubscriptionStatus.tsx
//
// This file defines the SubscriptionStatus component, which displays the user's current subscription tier (Free or Premium),
// and their usage of items and rooms relative to their plan limits. It also provides an upgrade button for free users.
//
// Usage:
//   - Typically used on the home or settings screen to show users their current plan and usage.
//   - Pulls data from SubscriptionContext (tier, item/room counts, limits) and uses ThemeContext for styling.
//   - The upgrade button navigates to the Paywall screen if the user is on the free plan.
//
// Features:
//   - Shows current plan (Free or Premium)
//   - Shows item and room usage with limits (e.g., 5 / 20)
//   - Highlights when a limit is reached
//   - Upgrade button is disabled for premium users
//   - Themed for light/dark mode
//
// Example:
//   <SubscriptionStatus />

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSubscription } from '../../lib/SubscriptionContext';
import type { RootStackParamList } from '../types/navigation';
import { useTheme } from '../../lib/ThemeContext';
import ThemedButton from './ThemedButton';

// Navigation type for navigating to Paywall
type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

// SubscriptionStatus component
export default function SubscriptionStatus() {
  const navigation = useNavigation<NavigationProp>();
  // Get subscription info and limits from context
  const { tier, itemCount, roomCount, itemLimit, roomLimit } = useSubscription();
  const { theme, themeType } = useTheme();

  const glowStyle = themeType === 'blueNeon'
    ? (Platform.OS === 'web'
        ? { boxShadow: `0 0 6px 1px ${theme.accent}` } as any
        : {
            shadowColor: theme.accent,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.5,
            shadowRadius: 6,
            elevation: 6,
          })
    : {};

  // Helper to format usage/limit display
  const formatLimit = (count: number, limit: number) => {
    if (limit === Infinity) {
      return `${count} / unlimited`;
    }
    return `${count} / ${limit}`;
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.card }, glowStyle]}>
      {/* Header row with title and upgrade button */}
      <View style={styles.header}>
        <View style={styles.titleContainer}>
          <Text style={[styles.titleGreen, { color: theme.text }]}>Subscription Status</Text>
        </View>
        <ThemedButton
          title={tier === 'premium' ? 'Premium' : 'Upgrade'}
          onPress={() => { if (tier === 'free') navigation.navigate('Paywall' as never); }}
          disabled={tier === 'premium'}
          style={{ paddingHorizontal: 12, paddingVertical: 6 }}
          textStyle={{ fontSize: 14 }}
        />
      </View>

      <View style={styles.content}>
        {/* Current plan section */}
        <View style={styles.section}>
          <View style={styles.sectionTitleContainer}>
            <Text style={[styles.sectionTitle, { color: theme.secondaryText }]}>Current Plan</Text>
          </View>
          <View style={styles.planContainer}>
            <Text style={[styles.planText, { color: theme.text }]}>
              {tier === 'free' ? 'Free' : 'Premium'}
            </Text>
          </View>
        </View>

        {/* Items used section */}
        <View style={styles.section}>
          <View style={styles.sectionTitleContainer}>
            <Text style={[styles.sectionTitle, { color: theme.secondaryText }]}>Items Used In Free Plan</Text>
          </View>
          <View style={styles.countContainer}>
            <Text style={[
              styles.countText,
              { color: theme.text },
              itemCount >= itemLimit && { color: theme.error }
            ]}>
              {formatLimit(itemCount, itemLimit)}
            </Text>
          </View>
        </View>

        {/* Rooms used section */}
        <View style={styles.section}>
          <View style={styles.sectionTitleContainer}>
            <Text style={[styles.sectionTitle, { color: theme.secondaryText }]}>Rooms Used In Free Plan</Text>
          </View>
          <View style={styles.countContainer}>
            <Text style={[
              styles.countText,
              { color: theme.text },
              roomCount >= roomLimit && { color: theme.error }
            ]}>
              {formatLimit(roomCount, roomLimit)}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
}

// Styles for the subscription status card and text
const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  titleContainer: {
    flex: 1,
  },
  titleGreen: {
    fontSize: 17,
    fontWeight: '500',
  },
  upgradeButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  upgradeButtonDisabled: {
    backgroundColor: '#555',
  },
  upgradeButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  content: {
    gap: 12,
  },
  section: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionTitleContainer: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 16,
  },
  planContainer: {
    alignItems: 'flex-end',
  },
  planText: {
    fontSize: 16,
    fontWeight: '500',
  },
  countContainer: {
    alignItems: 'flex-end',
  },
  countText: {
    fontSize: 16,
    fontWeight: '500',
  },
  limitReached: {
    color: '#ff3b30',
  },
}); 